import React, { useContext, useEffect, useMemo, useRef } from "react";
import { getPeriodKey } from "@common/helpers/reports";
import { TrialBalanceResponse } from "@common/types/reports/types";
import { ViewTable, ViewTableHandle } from "@design-system/ViewTable/ViewTable";
import { AccountingReportContext } from "../AccountingReport.context";
import { GlTableCallbacks, GlTableMeta } from "../glDrillDown.lib";
import { hasSingleValueColumn } from "../singleColumnReport";
import { buildTrialBalanceRows } from "./trialBalanceViewBuilder";
import {
    buildTrialBalanceConfig,
    buildTrialBalanceView,
} from "./trialBalanceViewConfig";
import styles from "./TrialBalanceTable.module.scss";

interface Props {
    data: TrialBalanceResponse;
    onDrillDown: GlTableCallbacks["onDrillDown"];
}

export const TrialBalanceTable: React.FC<Props> = ({ data, onDrillDown }) => {
    const ref = useRef<ViewTableHandle>(null);
    const { numberFormat } = useContext(AccountingReportContext);
    const rows = useMemo(() => buildTrialBalanceRows(data), [data]);
    const view = useMemo(
        () => buildTrialBalanceView(data.periods),
        [data.periods],
    );
    const tableMeta: GlTableMeta = useMemo(
        () => ({ callbacks: { onDrillDown } }),
        [onDrillDown],
    );
    const config = useMemo(
        () => buildTrialBalanceConfig(data.periods, numberFormat),
        [data.periods, numberFormat],
    );
    const reportKey = useMemo(
        () => data.periods.map(getPeriodKey).join("|"),
        [data.periods],
    );

    useEffect(() => {
        const t = window.setTimeout(
            () => ref.current?.scrollTo({ left: Number.MAX_SAFE_INTEGER }),
            0,
        );
        return () => window.clearTimeout(t);
    }, [reportKey]);

    return (
        <ViewTable
            key={reportKey}
            ref={ref}
            data={rows}
            view={view}
            config={config}
            tableMeta={tableMeta}
            className={styles.trialBalanceTable}
            centerColumns={hasSingleValueColumn(data.periods)}
            rowBorders={false}
            enableColumnResizing
            nestingIndentPx={0}
            fixedTopOffsetPx
        />
    );
};
